
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog"
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { ImageUpload } from "@/components/ui/image-upload"
import { courseService } from "@/services/course.service"
import { useToast } from "@/hooks/use-toast"
import { Loader2 } from "lucide-react"
import type { Category } from "@/types/api"

const formSchema = z.object({
    title: z.string().min(2, "Название должно содержать не менее 2 символов"),
    slug: z.string().optional().refine(
        (val) => !val || (val.length >= 2 && val.length <= 50 && /^[a-z0-9-]+$/.test(val)),
        "Слаг может содержать только английские буквы (a-z), цифры и дефисы (макс. 50 символов)"
    ),
    icon: z.any().optional(),
})

interface CategoryDialogProps {
    category: Category | null
    open: boolean
    onOpenChange: (open: boolean) => void
    onSuccess: () => void
}

export function CategoryDialog({
    category,
    open,
    onOpenChange,
    onSuccess,
}: CategoryDialogProps) {
    const { toast } = useToast()
    const [isLoading, setIsLoading] = useState(false)
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: "",
            slug: "",
            icon: null,
        },
    })

    useEffect(() => {
        if (category) {
            form.reset({
                title: category.title,
                slug: undefined,
                icon: category.icon,
            })
        } else {
            form.reset({
                title: "",
                slug: "",
                icon: null,
            })
        }
    }, [category, form, open])

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        setIsLoading(true)
        try {
            const formData = new FormData()
            formData.append("title", values.title)
            if (!category && values.slug) {
                formData.append("slug", values.slug)
            }
            if (values.icon instanceof File) {
                formData.append("icon", values.icon)
            }

            if (category) {
                await courseService.updateCategory(category.slug, formData)
                toast({
                    title: "Успешно",
                    description: "Категория успешно обновлена",
                })
            } else {
                await courseService.createCategory(formData)
                toast({
                    title: "Успешно",
                    description: "Категория успешно создана",
                })
            }
            onSuccess()
            onOpenChange(false)
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: `Не удалось ${category ? "обновить" : "создать"} категорию`,
                variant: "destructive",
            })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{category ? "Редактировать категорию" : "Создать категорию"}</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="icon"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Иконка</FormLabel>
                                    <FormControl>
                                        <ImageUpload
                                            value={field.value}
                                            onChange={field.onChange}
                                            label="Загрузить иконку"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="title"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Название</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {!category && (
                            <FormField
                                control={form.control}
                                name="slug"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Слаг</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="web-development" maxLength={50} />
                                        </FormControl>
                                        <FormDescription>
                                            Используйте только английские буквы, цифры и дефисы (макс. 50 символов)
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}
                        <DialogFooter>
                            <Button type="submit" disabled={isLoading}>
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {category ? "Сохранение..." : "Создание..."}
                                    </>
                                ) : (
                                    category ? "Сохранить изменения" : "Создать"
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
